import { useCallback } from 'react'
import {
  ColorModifier,
  ColorModifierTypes,
  ColorSpaceTypes,
  SingleGenericToken,
  TokenTypes,
} from '@tokens-studio/types'
import { Button, Input, Select } from '@dashboard/ui'

import { FieldGroup } from '@/ui'
import { getModifyColorSpaces, getModifyColorTypes } from '../../libs/modify'

interface FormModifyColorControlProps {
  value: SingleGenericToken<TokenTypes.COLOR>['$extensions']
  onChange: (value: SingleGenericToken<TokenTypes.COLOR>['$extensions']) => void
}

export const FormModifyColorControl = ({ value, onChange }: FormModifyColorControlProps) => {
  const modify = value?.['studio.tokens']?.modify

  const onClickAddModify = useCallback(() => {
    onChange({
      ...value,
      ['studio.tokens']: {
        ...value?.['studio.tokens'],
        modify: {
          type: ColorModifierTypes.DARKEN,
          space: ColorSpaceTypes.LCH,
          value: '0.25',
          format: 'hex',
        },
      },
    })
  }, [onChange, value])

  const onClickRemoveModify = useCallback(() => {
    onChange({
      ...value,
      ['studio.tokens']: {
        ...value?.['studio.tokens'],
        modify: undefined,
      },
    })
  }, [onChange, value])

  const onModifyChange = useCallback(
    (modify: ColorModifier) => {
      onChange({
        ...value,
        ['studio.tokens']: { ...value?.['studio.tokens'], modify },
      })
    },
    [onChange, value],
  )

  return (
    <div className="rounded-md border bg-fg-secondary">
      <div className="flex justify-between p-3">
        <div className="font-medium text-text-secondary">Modification</div>
        {modify ? (
          <Button variant="link" onClick={onClickRemoveModify}>
            remove
          </Button>
        ) : (
          <Button variant="link" onClick={onClickAddModify}>
            add
          </Button>
        )}
      </div>

      {!!modify && (
        <div className="border-t p-3">
          <div className="grid w-full grid-cols-2 gap-3">
            <FieldGroup label="Operation">
              <Select
                items={getModifyColorTypes().map(operation => ({ value: operation, children: operation }))}
                value={modify?.type}
                // @ts-expect-error
                onChange={value => onModifyChange({ ...modify, type: value as ColorModifierTypes })}
              />
            </FieldGroup>

            <FieldGroup label="Space">
              <Select
                items={getModifyColorSpaces().map(space => ({ value: space, children: space }))}
                value={modify?.space}
                onChange={value => onModifyChange({ ...modify, space: value as ColorSpaceTypes })}
              />
            </FieldGroup>
          </div>

          <FieldGroup label="amount">
            <Input
              type="number"
              step={0.01}
              min={0}
              max={1}
              value={modify?.value}
              onChange={value => onModifyChange({ ...modify, value: value.target.value })}
            />
          </FieldGroup>
        </div>
      )}
    </div>
  )
}
